import { setDoc, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import axiosInstance from '../axiosInstance';

const get = async () => {
  return axiosInstance.get('/event').then((response) => {
    return response.data;
  });
};

/**
 * One page of the events list. The scope filter runs on the server because
 * the list only loads the first page, so the client cannot filter the rest
 * away. `today` and `tzOffset` are the user's local day, so events dated at
 * local midnight are not shifted by the server's UTC clock.
 * @param {Object} params - { scope, limit, offset }
 * @returns {Promise<{ events: Object[], total: Number, offset: Number, limit: Number, hasMore: Boolean }>}
 */
const getPage = async (params = {}) => {
  return axiosInstance
    .get('/event', {
      params: {
        today: moment().format('YYYY-MM-DD'),
        tzOffset: -new Date().getTimezoneOffset(),
        ...params
      }
    })
    .then((response) => response.data);
};

const getAllByVenue = async (venueId) => {
  return axiosInstance.get(`/event/venue/${venueId}`).then((response) => {
    return response.data;
  });
};

const getById = async (eventId) => {
  return axiosInstance.get(`/event/${eventId}`).then((response) => {
    return response.data;
  });
};

const getEventScoreMethod = async (eventId) => {
  return axiosInstance.get(`/event/${eventId}/score-method`).then((response) => {
    return response.data;
  });
};

// Sections, players, teams and legacy games below write the event document
// straight to Firestore, so Express middleware cannot see them. A restore
// point has to be taken by the client just before the write. Failures are
// swallowed: a history problem must never stop an organiser saving.
//
// Any future direct setDoc/updateDoc on an event must call
// captureBeforeDirectWrite first. The in-flight map keeps two quick edits in
// one dialog from producing two nearly identical restore points.
const pendingSnapshotRequests = new Map();

const requestAutomaticSnapshot = async (eventID, operation) => {
  if (!eventID) {
    return null;
  }
  try {
    const response = await axiosInstance.post(`/event/${eventID}/snapshots/auto`, {
      operation
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

const captureBeforeDirectWrite = (eventID, operation, options = {}) => {
  if (!eventID || options.skipSnapshot) {
    return Promise.resolve(null);
  }
  const key = String(eventID);
  if (pendingSnapshotRequests.has(key)) {
    return pendingSnapshotRequests.get(key);
  }
  const request = requestAutomaticSnapshot(eventID, operation).finally(() => {
    pendingSnapshotRequests.delete(key);
  });
  pendingSnapshotRequests.set(key, request);
  return request;
};

// Explicit capture for a batched import. The per-write coalescer would make
// the point depend on whichever batch happened to be first; the import takes
// one point before it starts and passes skipSnapshot to the batch writes.
const captureEventSnapshot = (eventID, operation) => requestAutomaticSnapshot(eventID, operation);

const personName = (person) =>
  [String(person?.firstName || '').trim(), String(person?.lastName || '').trim()]
    .filter(Boolean)
    .join(' ')
    .trim() ||
  String(person?.name || person?.fullName || '').trim() ||
  null;

const gameLabel = (game, section) => {
  const teams = Array.isArray(game?.teams) ? game.teams : [];
  const teamNames = teams
    .map((team) => String(team?.name || '').trim())
    .filter(Boolean);
  return (
    teamNames.join(' v ') ||
    String(game?.name || section?.name || '').trim() ||
    null
  );
};

// Reports a completed direct Firestore write to the event audit log. It is
// deliberately fire-and-forget: an audit problem must never block or fail a
// save, and the server ignores a missing or failed record.
const reportDirectWrite = (eventID, operation, details = {}) => {
  if (!eventID || details.skipAudit) {
    return;
  }

  const body = {
    operation,
    entityId: details.entityId || null,
    entityName: details.entityName || null,
    snapshotId: details.snapshotId || null,
    metadata: details.metadata || {}
  };

  axiosInstance.post(`/event/${eventID}/audit/direct`, body).catch(() => null);
};

const create = async (event) => {
  return axiosInstance.post('/event', event).then((response) => {
    return response.data;
  });
};

const duplicate = async (eventId) => {
  return axiosInstance.post(`/event/${eventId}/duplicate`).then((response) => {
    return response.data;
  });
};

const transfer = async (eventId, workspaceId) => {
  return axiosInstance
    .post(`/event/${eventId}/transfer`, { targetWorkspaceId: workspaceId })
    .then((response) => {
      return response.data;
    });
};

const update = async (eventId, event) => {
  return axiosInstance.put(`/event/${eventId}`, event).then((response) => {
    return response.data;
  });
};

const remove = async (eventId) => {
  return axiosInstance.delete(`/event/${eventId}`).then((response) => {
    return response.data;
  });
};

const updateTeam = async (eventId, team) => {
  return axiosInstance.put(`/event/${eventId}/team`, { team }).then((response) => {
    return response.data;
  });
};

const finaliseSection = async (eventId, sectionId) => {
  return axiosInstance.put(`/event/${eventId}/finalise-section`, { sectionId }).then((response) => {
    return response.data;
  });
};

const addSection = async (eventID, section) => {
  // id: uuid, name:String, players:[array], games:[array], rounds:Number,
  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();
  const generatedSectionId = uuidv4();

  section.id = generatedSectionId;

  if (!Array.isArray(event.sections)) {
    event.sections = [];
  }
  event.sections.push(section);

  const snapshot = await captureBeforeDirectWrite(eventID, 'section.add');
  await setDoc(docRef, event);
  reportDirectWrite(eventID, 'section.add', {
    entityId: section.id,
    entityName: section.name,
    snapshotId: snapshot?.snapshotId
  });
};

const updateSection = async (eventID, section) => {
  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();

  const sectionIndex = event.sections.findIndex((s) => s.id === section.id);
  const updatedSection = { ...event.sections[sectionIndex], ...section };
  event.sections[sectionIndex] = updatedSection;

  const snapshot = await captureBeforeDirectWrite(eventID, 'section.update');
  await setDoc(docRef, event);
  reportDirectWrite(eventID, 'section.update', {
    entityId: updatedSection.id,
    entityName: updatedSection.name,
    snapshotId: snapshot?.snapshotId
  });
};

const addPlayer = async (eventID, player, options = {}) => {
  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();

  if (!player.id) {
    const generatedPlayerId = uuidv4();
    player.id = generatedPlayerId;
  }

  if (!Array.isArray(event.players)) {
    event.players = [];
  }
  event.players.push(player);
  const snapshot = await captureBeforeDirectWrite(eventID, 'player.add', options);
  await setDoc(docRef, event);
  reportDirectWrite(eventID, 'player.add', {
    entityId: player.id,
    entityName: personName(player),
    snapshotId: snapshot?.snapshotId,
    skipAudit: options.skipAudit
  });
};

const updatePlayer = async (eventID, player) => {
  console.log('player', player);

  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();

  const playerIndex = event.players.findIndex((p) => p.id === player.id);
  event.players[playerIndex] = player;

  const snapshot = await captureBeforeDirectWrite(eventID, 'player.update');
  await setDoc(docRef, event);
  reportDirectWrite(eventID, 'player.update', {
    entityId: player.id,
    entityName: personName(player),
    snapshotId: snapshot?.snapshotId
  });
};

const removePlayer = async (eventID, playerID) => {
  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();

  const playerIndex = event.players.findIndex((p) => p.id === playerID);
  const removedPlayer = playerIndex >= 0 ? event.players[playerIndex] : null;
  event.players.splice(playerIndex, 1);

  const section = event.sections.find((section) => section.players.find((p) => p.id === playerID));

  const sectionPlayerIndex = section.players.findIndex((p) => p.id === playerID);

  section.players.splice(sectionPlayerIndex, 1);

  const snapshot = await captureBeforeDirectWrite(eventID, 'player.remove');
  await setDoc(docRef, event);
  reportDirectWrite(eventID, 'player.remove', {
    entityId: playerID,
    entityName: personName(removedPlayer),
    snapshotId: snapshot?.snapshotId
  });
};

const addTeam = async (eventID, team, options = {}) => {
  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();

  const generatedTeamId = uuidv4();
  team.id = generatedTeamId;

  if (!Array.isArray(event.teams)) {
    event.teams = [];
  }
  event.teams.push(team);
  const snapshot = await captureBeforeDirectWrite(eventID, 'team.add', options);
  await setDoc(docRef, event);
  reportDirectWrite(eventID, 'team.add', {
    entityId: team.id,
    entityName: team.name,
    snapshotId: snapshot?.snapshotId,
    skipAudit: options.skipAudit
  });
};

const addTeamGroup = async (eventID, group) => {
  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();

  const name = String(group?.name || '').trim();
  if (!name) {
    return null;
  }

  if (!group.id) {
    group.id = uuidv4();
  }

  if (!Array.isArray(event.teamGroups)) {
    event.teamGroups = [];
  }

  const alreadyExists = event.teamGroups.some(
    (existing) =>
      String(existing.name || '')
        .trim()
        .toLowerCase() === name.toLowerCase()
  );

  if (!alreadyExists) {
    event.teamGroups.push({ id: group.id, name });
    const snapshot = await captureBeforeDirectWrite(eventID, 'team.group');
    await setDoc(docRef, event);
    reportDirectWrite(eventID, 'team.group', {
      entityId: group.id,
      entityName: name,
      snapshotId: snapshot?.snapshotId
    });
  }

  return { id: group.id, name };
};

// const updateTeam = async (eventID, team) => {
//   const docRef = doc(store, 'events', eventID);
//   const docSnap = await getDoc(docRef);
//   const event = docSnap.data();

//   const teamIndex = event.teams.findIndex((t) => t.id === team.id);
//   event.teams[teamIndex] = team;

//   await setDoc(docRef, event);
// };

const removeTeam = async (eventID, teamID) => {
  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();

  const teamIndex = event.teams.findIndex((p) => p.id === teamID);
  const removedTeam = teamIndex >= 0 ? event.teams[teamIndex] : null;
  event.teams.splice(teamIndex, 1);

  const section = event.sections.find((section) => section.teams.find((t) => t.id === teamID));

  const sectionTeamIndex = section.teams.findIndex((t) => t.id === teamID);
  section.teams.splice(sectionTeamIndex, 1);
  const snapshot = await captureBeforeDirectWrite(eventID, 'team.remove');
  await setDoc(docRef, event);
  reportDirectWrite(eventID, 'team.remove', {
    entityId: teamID,
    entityName: removedTeam?.name,
    snapshotId: snapshot?.snapshotId
  });
};

// const addGame = async (eventID, sectionID, game) => {
//   try {
//     console.log({ eventID, game });
//     const docRef = doc(store, 'events', eventID);
//     const docSnap = await getDoc(docRef);
//     const event = docSnap.data();

//     const section = event.sections.find((section) => section.id === sectionID);

//     if (!Array.isArray(section.games)) {
//       section.games = [];
//     }

//     section.games.push(game);

//     await setDoc(docRef, event);

//     return game;
//   } catch (err) {
//     throw err;
//   }
// };

const updateGame = async (eventID, game) => {
  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();

  const section = event.sections.find((section) => section.id === game.section);

  const gameIndex = section.games.findIndex((g) => g.id === game.id);
  section.games[gameIndex] = game;

  const snapshot = await captureBeforeDirectWrite(eventID, 'game.update');
  await setDoc(docRef, event);
  reportDirectWrite(eventID, 'game.update', {
    entityId: game.id,
    entityName: gameLabel(game, section),
    snapshotId: snapshot?.snapshotId
  });
};

const deleteGame = async (eventID, gameID) => {
  const docRef = doc(store, 'events', eventID);
  const docSnap = await getDoc(docRef);
  const event = docSnap.data();

  const section = event.sections.find((section) => section.games.find((g) => g.id === gameID));

  const gameIndex = section.games.findIndex((g) => g.id === gameID);
  const removedGame = gameIndex >= 0 ? section.games[gameIndex] : null;
  section.games.splice(gameIndex, 1);

  const snapshot = await captureBeforeDirectWrite(eventID, 'game.delete');
  await setDoc(docRef, event);
  reportDirectWrite(eventID, 'game.delete', {
    entityId: gameID,
    entityName: gameLabel(removedGame, section),
    snapshotId: snapshot?.snapshotId
  });
};

const getCMEvents = async (cmClubId) => {
  return axiosInstance
    .get(`https://api.club-master.co.za/clubs/${cmClubId}/events`)
    .then((response) => {
      console.log('Club Master Events:', response.data);
      return response.data;
    });
};

const getCMEvent = async (cmEventId) => {
  return axiosInstance.get(`https://api.club-master.co.za/events/${cmEventId}`).then((response) => {
    return response.data;
  });
};

const updateCMEvent = async (cmEventId, bsEventId) => {
  return axiosInstance
    .put(`https://api.club-master.co.za/events/${cmEventId}`, { bowlsScoutId: bsEventId })
    .then((response) => {
      return response.data;
    });
};

const splitSection = async (eventId, sectionId, data) => {
  return axiosInstance
    .put(`/event/${eventId}/split-section/${sectionId}`, data)
    .then((response) => {
      return response.data;
    });
};

const deleteSection = async (eventId, sectionId) => {
  return axiosInstance.delete(`/event/${eventId}/section/${sectionId}`).then((response) => {
    return response.data;
  });
};

const greenSetup = async (eventId, locations) => {
  return axiosInstance.put(`/event/${eventId}/green-setup`, { locations }).then((response) => {
    return response.data;
  });
};

const sectionDrawSetup = async (eventId, sectionId, teams) => {
  return axiosInstance
    .put(`/event/${eventId}/section-draw-setup/${sectionId}`, { teams })
    .then((response) => {
      return response.data;
    });
};

const generateSections = async (eventId, data) => {
  return axiosInstance.post(`/event/${eventId}/generate-sections`, data).then((response) => {
    return response.data;
  });
};

const nextRound = async (eventId) => {
  return axiosInstance.put(`/event/${eventId}/next-round`).then((response) => {
    return response.data;
  });
};

const playerOpponents = async (eventId) => {
  return axiosInstance.get(`/event/${eventId}/player-opponents`).then((response) => {
    return response.data;
  });
};

const swapTeamsInGames = async (eventId, teamA, teamB) => {
  return axiosInstance.put(`/event/${eventId}/swap-teams`, { teamA, teamB }).then((response) => {
    return response.data;
  });
};

// Event-wide corrections. Every preview is read-only and returns a hash the
// apply call must present; the dialogs and AI tools never apply blind.
const getVenueUsage = async (eventId) => {
  return axiosInstance.get(`/event/${eventId}/venue-usage`).then((response) => {
    return response.data;
  });
};

const searchPlayers = async (eventId, query, limit = 25) => {
  return axiosInstance
    .get(`/event/${eventId}/player-search`, { params: { q: query, limit } })
    .then((response) => {
      return response.data;
    });
};

const previewPlayerReplacement = async (eventId, payload) => {
  return axiosInstance
    .post(`/event/${eventId}/player-replacement/preview`, payload)
    .then((response) => {
      return response.data;
    });
};

const applyPlayerReplacement = async (eventId, payload) => {
  return axiosInstance
    .post(`/event/${eventId}/player-replacement/apply`, payload)
    .then((response) => {
      return response.data;
    });
};

const previewReservePromotion = async (eventId, payload) => {
  return axiosInstance
    .post(`/event/${eventId}/reserve-promotion/preview`, payload)
    .then((response) => {
      return response.data;
    });
};

const applyReservePromotion = async (eventId, payload) => {
  return axiosInstance
    .post(`/event/${eventId}/reserve-promotion/apply`, payload)
    .then((response) => {
      return response.data;
    });
};

const previewVenueSwap = async (eventId, payload) => {
  return axiosInstance.post(`/event/${eventId}/venue-swap/preview`, payload).then((response) => {
    return response.data;
  });
};

const applyVenueSwap = async (eventId, payload) => {
  return axiosInstance.post(`/event/${eventId}/venue-swap/apply`, payload).then((response) => {
    return response.data;
  });
};

// Event history. The list, save, delete and restore calls answer with the same
// { success, code, message, details } envelope as the event-wide corrections;
// the automatic capture endpoint is called by captureBeforeDirectWrite above
// and intentionally hides its own failures.
const getEventSnapshots = async (eventId, params = {}) => {
  return axiosInstance.get(`/event/${eventId}/snapshots`, { params }).then((response) => {
    return response.data;
  });
};

const saveEventSnapshot = async (eventId, payload = {}) => {
  return axiosInstance.post(`/event/${eventId}/snapshots`, payload).then((response) => {
    return response.data;
  });
};

const deleteEventSnapshot = async (eventId, snapshotId) => {
  return axiosInstance
    .delete(`/event/${eventId}/snapshots/${snapshotId}`)
    .then((response) => response.data);
};

const previewEventSnapshotRestore = async (eventId, snapshotId) => {
  return axiosInstance
    .post(`/event/${eventId}/snapshots/${snapshotId}/restore/preview`)
    .then((response) => response.data);
};

const applyEventSnapshotRestore = async (eventId, snapshotId, payload) => {
  return axiosInstance
    .post(`/event/${eventId}/snapshots/${snapshotId}/restore/apply`, payload)
    .then((response) => response.data);
};

const eventService = {
  get,
  getPage,
  getById,
  getAllByVenue,
  create,
  duplicate,
  transfer,
  update,
  remove,
  updateTeam,
  finaliseSection,
  addSection,
  updateSection,
  splitSection,
  deleteSection,
  addPlayer,
  updatePlayer,
  removePlayer,
  addTeam,
  addTeamGroup,
  //updateTeam,
  removeTeam,
  // addGame,
  updateGame,
  deleteGame,
  getCMEvents,
  getCMEvent,
  updateCMEvent,
  greenSetup,
  sectionDrawSetup,
  generateSections,
  nextRound,
  playerOpponents,
  swapTeamsInGames,
  getEventScoreMethod,
  getVenueUsage,
  searchPlayers,
  previewPlayerReplacement,
  applyPlayerReplacement,
  previewReservePromotion,
  applyReservePromotion,
  previewVenueSwap,
  applyVenueSwap,
  reportDirectWrite,
  captureEventSnapshot,
  getEventSnapshots,
  saveEventSnapshot,
  deleteEventSnapshot,
  previewEventSnapshotRestore,
  applyEventSnapshotRestore
};

export default eventService;
